/**
 * Orgus Watch incidents inside Needs you (ADR 0058 decision 3, as amended): one place for
 * everything that needs a person, and each thing said once. An incident and a derived row can
 * describe the same thing: a stuck session, a worktree left with uncommitted work, a branch whose
 * landing failed. Then only one shows. Pure, so tested.
 */
import type { Incident, IncidentDetector } from '@orgus/protocol';
import type { NeedSubject } from '../overview/needsYou.ts';

/**
 * The detectors whose incident repeats a derived row, and what the incident is about. Only these:
 * a refusal storm on a machine is not the machine going silent, and a context-heavy session is
 * not a stuck one, so those rows both stay.
 */
const SUBJECT_OF: Partial<Record<IncidentDetector, (i: Incident) => NeedSubject | null>> = {
  'stuck-session': (i) => (i.scope.sessionId ? { kind: 'session', id: i.scope.sessionId } : null),
  'orphaned-work': (i) => (i.scope.worktree ? { kind: 'worktree', id: i.scope.worktree } : null),
  'landing-failed': (i) => (i.scope.branch ? { kind: 'branch', id: i.scope.branch } : null),
};

/** What an incident is about, when a derived row could say the same; null otherwise. */
export function incidentSubject(i: Incident): NeedSubject | null {
  return SUBJECT_OF[i.detector]?.(i) ?? null;
}

const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();

/** Same kind and the same thing: worktree folders compare as paths (slashes and case aside). */
export function sameSubject(a: NeedSubject, b: NeedSubject): boolean {
  if (a.kind !== b.kind) return false;
  return a.kind === 'worktree' ? norm(a.id) === norm(b.id) : a.id === b.id;
}

/**
 * Which side wins a repeat: the incident while the watch is running (it carries the action and
 * the evidence), the derived row while the watch is stale (the row is read live; the incident
 * may be out of date). With no watch there are no incidents to repeat.
 */
export type RepeatWinner = 'incident' | 'derived';

/** The derived rows and incidents with every repeat said once, by the winner. Order is kept. */
export function dedupeWithIncidents<N extends { key: string; subject?: NeedSubject | null }>(
  needs: readonly N[],
  incidents: readonly Incident[],
  winner: RepeatWinner,
): { needs: N[]; incidents: Incident[] } {
  const subjects = incidents.map((i) => ({ i, s: incidentSubject(i) }));
  const repeats = (n: N) => (n.subject ? subjects.filter((x) => x.s && sameSubject(x.s, n.subject!)).map((x) => x.i) : []);
  if (winner === 'incident') return { needs: needs.filter((n) => repeats(n).length === 0), incidents: [...incidents] };
  const lost = new Set(needs.flatMap((n) => repeats(n).map((i) => i.id)));
  return { needs: [...needs], incidents: incidents.filter((i) => !lost.has(i.id)) };
}
